import React from 'react'

interface UserRankBadgeProps {
  rank: string
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
  className?: string
}

// Custom SVG Icons for each rank
const RankIcons = {
  Admin: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1l2.5 5h5.5l-4.5 3.5 1.5 5.5L8 11l-4.5 3.5 1.5-5.5L0 6.5h5.5L8 1z"/>
      <circle cx="8" cy="8" r="2" fill="white" fillOpacity="0.3"/>
    </svg>
  ),
  'Co-Admin': ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 0L6 6H0l5 4-2 6 5-4 5 4-2-6 5-4h-6L8 0z"/>
      <path d="M8 4v8" stroke="white" strokeWidth="1" fillOpacity="0.5"/>
    </svg>
  ),
  Moderator: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 2L6 6H2l3 2.5L4 12l4-2.5L12 12l-1-3.5L14 6h-4l-2-4z"/>
      <rect x="6" y="6" width="4" height="4" fill="white" fillOpacity="0.3"/>
    </svg>
  ),
  Helper: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 2a6 6 0 100 12A6 6 0 008 2z"/>
      <path d="M6 7l2 2 4-4" stroke="white" strokeWidth="1.5" fill="none"/>
    </svg>
  ),
  Grandmaster: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1l1.5 3h3.5l-2.5 2 1 3.5L8 7.5 4.5 9.5l1-3.5L3 4h3.5L8 1z"/>
      <path d="M8 1l0.75 1.5h1.75l-1.25 1 0.5 1.75L8 4.25 6.25 5.25l0.5-1.75-1.25-1h1.75L8 1z" fill="white" fillOpacity="0.7"/>
    </svg>
  ),
  'Math Master': ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 2l1.5 2.5 3-.5-1.5 2.5 1.5 2.5-3-.5L8 11l-1.5-2.5-3 .5 1.5-2.5L3 4l3 .5L8 2z"/>
      <circle cx="8" cy="6.5" r="1.5" fill="white" fillOpacity="0.4"/>
    </svg>
  ),
  Mathematician: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 3l1 2h2l-1.5 1.5L10.5 9 8 7.5 5.5 9l1-2.5L5 5h2l1-2z"/>
      <path d="M4 12h8" stroke="currentColor" strokeWidth="1"/>
    </svg>
  ),
  Solver: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 4l1 2 2-1-1 2 2 1-2 1 1 2-2-1-1 2-1-2-2 1 1-2-2-1 2-1-1-2 2 1 1-2z"/>
    </svg>
  ),
  User: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <circle cx="8" cy="5" r="3"/>
      <path d="M2 14s0-4 6-4 6 4 6 4"/>
    </svg>
  ),
} as const

const RANK_CONFIG = {
  'Admin': {
    color: 'text-pink-400 bg-gradient-to-r from-pink-900/40 to-purple-900/40 border-pink-400/70 shadow-lg shadow-pink-500/30',
    icon: RankIcons.Admin,
    description: 'Administrator',
    special: true
  },
  'Co-Admin': {
    color: 'text-red-400 bg-gradient-to-r from-red-900/40 to-orange-900/40 border-red-400/70 shadow-md shadow-red-500/30',
    icon: RankIcons['Co-Admin'],
    description: 'Co-Administrator',
    special: true
  },
  'Moderator': {
    color: 'text-orange-400 bg-gradient-to-r from-orange-900/40 to-yellow-900/40 border-orange-400/70',
    icon: RankIcons.Moderator,
    description: 'Community Moderator',
    special: false
  },
  'Helper': {
    color: 'text-cyan-400 bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border-cyan-400/70',
    icon: RankIcons.Helper,
    description: 'Community Helper',
    special: false
  },
  'Grandmaster': {
    color: 'text-yellow-400 bg-gradient-to-r from-yellow-900/40 to-amber-900/40 border-yellow-400/70 shadow-md shadow-yellow-500/20',
    icon: RankIcons.Grandmaster,
    description: 'Elite Mathematician',
    special: true
  },
  'Math Master': {
    color: 'text-purple-400 bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border-purple-400/70',
    icon: RankIcons['Math Master'],
    description: 'Expert Mathematician',
    special: false
  },
  'Mathematician': {
    color: 'text-blue-400 bg-gradient-to-r from-blue-900/40 to-cyan-900/40 border-blue-400/70',
    icon: RankIcons.Mathematician,
    description: 'Skilled Mathematician',
    special: false
  },
  'Solver': {
    color: 'text-green-400 bg-green-900/30 border-green-400/70',
    icon: RankIcons.Solver,
    description: 'Active Problem Solver',
    special: false
  },
  'User': {
    color: 'text-gray-400 bg-gray-900/30 border-gray-500/50',
    icon: RankIcons.User,
    description: 'Member',
    special: false
  }
} as const

export default function UserRankBadge({ 
  rank, 
  size = 'md', 
  showLabel = true,
  className = ''
}: UserRankBadgeProps) {
  const config = RANK_CONFIG[rank as keyof typeof RANK_CONFIG] || RANK_CONFIG['User']
  const Icon = config.icon

  const sizeClasses = {
    sm: {
      container: 'px-2 py-1 text-xs',
      icon: 'h-3 w-3',
      text: 'text-xs'
    },
    md: {
      container: 'px-3 py-1 text-sm',
      icon: 'h-4 w-4',
      text: 'text-sm'
    },
    lg: {
      container: 'px-4 py-2 text-base',
      icon: 'h-5 w-5',
      text: 'text-base'
    }
  }

  const sizes = sizeClasses[size]

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${config.color} ${sizes.container} ${className} ${config.special ? 'animate-pulse' : ''}`}
      title={config.description}
    >
      <Icon className={sizes.icon} />
      {showLabel && (
        <span className={sizes.text}>{rank}</span>
      )}
    </span>
  )
}

// Helper function to get rank color class
export function getRankColorClass(rank: string): string {
  const config = RANK_CONFIG[rank as keyof typeof RANK_CONFIG] || RANK_CONFIG['User']
  return config.color.split(' ')[0] // Just the text color part
}

// Helper function to check if rank has special privileges
export function hasModeratorPrivileges(rank: string): boolean {
  return ['Admin', 'Co-Admin', 'Moderator', 'Helper'].includes(rank)
}

export function hasAdminPrivileges(rank: string): boolean {
  return ['Admin', 'Co-Admin'].includes(rank)
}